import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

interface UIState {
  mobileDrawerOpen: boolean;
  sidebarOpen: boolean; // Alias for backward compatibility
  sidebarCollapsed: boolean;
  currentView: string;
  theme: 'light' | 'dark';

  setMobileDrawerOpen: (open: boolean) => void;
  toggleMobileDrawer: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapsed: () => void;
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setView: (view: string) => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        mobileDrawerOpen: false,
        get sidebarOpen() {
          return this.mobileDrawerOpen;
        },
        sidebarCollapsed: false,
        currentView: 'dashboard',
        theme: 'light',

        setMobileDrawerOpen: (open) => set({ mobileDrawerOpen: open }),
        toggleMobileDrawer: () => set((state) => ({ mobileDrawerOpen: !state.mobileDrawerOpen })),

        setSidebarOpen: (open) => set({ mobileDrawerOpen: open }),
        toggleSidebar: () => set((state) => ({ mobileDrawerOpen: !state.mobileDrawerOpen })),

        setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
        toggleSidebarCollapsed: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

        toggleTheme: () =>
          set((state) => {
            const newTheme = state.theme === 'light' ? 'dark' : 'light';
            if (newTheme === 'dark') {
              document.documentElement.classList.add('dark');
            } else {
              document.documentElement.classList.remove('dark');
            }
            return { theme: newTheme };
          }),

        setTheme: (theme) => {
          if (theme === 'dark') {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
          set({ theme });
        },

        setView: (view) => set({ currentView: view }),
      }),
      {
        name: 'depilaway-ui',
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          theme: state.theme,
        }),
        onRehydrateStorage: () => (state) => {
          if (state?.theme === 'dark') {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
        },
      }
    ),
    { name: 'UIStore' }
  )
);
